import React, { useState } from 'react';
import { GraduationCapIcon, LockIcon, RefreshCwIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { CaseQueue } from '../../components/verification/CaseQueue';
import { useVerification } from '../../contexts/VerificationContext';
import { STATUS_LABEL, formatDate, nextLifecycle } from '../../utils/verification';
const TABS = ['Requests', 'Structure', 'Lifecycle'] as const;

/** The institution this console is signed in as. Scope is not selectable here. */
const MY_INSTITUTION = 'inst-futa';
const OFFICER = '@futa.registry';

/**
 * The institution's own verification workspace. It confirms affiliation — the
 * strongest claim in the system — and can see nothing outside its own tenant.
 */
export function AcademicVerification() {
  const {
    institutions,
    records,
    reportStatus
  } = useVerification();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Requests');
  const institution = institutions.find(entry => entry.id === MY_INSTITUTION);
  const ourRecords = records.filter(record => record.institutionId === MY_INSTITUTION);
  return <div className="space-y-6" data-id="element-9075" data-mp="3spx7mmolklr3">
      <PageHeader eyebrow="Academic identity" title="{student} verification" description="Confirm that a person's claimed affiliation matches your own register. Your confirmation is the strongest verification Campuzon can hold." actions={<Badge plain data-id="element-9077" data-mp="3eokwqpoanac2">{institution?.name}</Badge>} data-id="element-9076" data-mp="3sovn7rloymwp" />

      <Panel data-id="element-9078" data-mp="3er7c3bhfeu4u">
        <p className="flex max-w-[80ch] items-start gap-2 text-body-sm text-ink" data-id="element-9079" data-mp="39qjc40b7z2yh">
          <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-9080" data-mp="39smdthlqikj1" />
          This console is scoped to {institution?.name}. Requests from other institutions are not
          reachable from here, and confirming a student gives you no access to their messages, notes,
          housing, financial records or activity elsewhere on Campuzon.
        </p>
      </Panel>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Verification sections" data-id="element-9081" data-mp="1vvjf68oegm2m" />

      {tab === 'Requests' && <CaseQueue institutionId={MY_INSTITUTION} reviewer={OFFICER} mode="institution" data-id="element-9082" data-mp="1i10jeg2yogta" />}

      {tab === 'Structure' && institution && <div className="space-y-4" data-id="element-9083" data-mp="2ask6wc6lch13">
          <Panel title="Your academic structure" description="These labels and values shape how Campuzon asks students about their record, and what it can verify." data-id="element-9084" data-mp="2apeeoo2o0z15">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9085" data-mp="20vfgg05ppzeh">
              <div data-id="element-9086" data-mp="25r4neok2aikt">
                <dt className="text-body-sm text-ink-muted" data-id="element-9087" data-mp="25uin1p1ccdok">Division label</dt>
                <dd className="text-body text-ink" data-id="element-9088" data-mp="pcl3zsfx3whf">{institution.structure.divisionLabel}</dd>
              </div>
              <div data-id="element-9089" data-mp="ueka3a31l7i2">
                <dt className="text-body-sm text-ink-muted" data-id="element-9090" data-mp="uawfs8prwh2a">Year of study label</dt>
                <dd className="text-body text-ink" data-id="element-9091" data-mp="mvuhgu8g9r5g">
                  {institution.structure.levelLabel ?? 'Not used'}
                </dd>
              </div>
              <div data-id="element-9092" data-mp="vtd6e9p7spmm">
                <dt className="text-body-sm text-ink-muted" data-id="element-9093" data-mp="vtwdswh8nvme">Identifier label</dt>
                <dd className="text-body text-ink" data-id="element-9094" data-mp="qweg628o92tp">{institution.structure.identifierLabel}</dd>
              </div>
              <div data-id="element-9095" data-mp="36aazqfu4v0l">
                <dt className="text-body-sm text-ink-muted" data-id="element-9096" data-mp="33o4zv8lenkh">Highest level supported</dt>
                <dd className="text-body text-ink" data-id="element-9097" data-mp="3vbe6ti602pf8">
                  {institution.maxLevelSupported.replace(/_/g, ' ')}
                </dd>
              </div>
            </dl>
            <div className="mt-4 border-t border-line pt-4" data-id="element-9098" data-mp="83sjxgtura8l">
              <h2 className="text-body font-semibold text-ink" data-id="element-9099" data-mp="3gkdn2gzsz93s">Statuses you recognise</h2>
              <ul className="mt-2 flex flex-wrap gap-2" data-id="element-9100" data-mp="3blk9ypofhk45">
                {institution.structure.statuses.map(status => <li key={status} data-id="element-9101" data-mp="3p92h0lgh52zn">
                    <Badge plain data-id="element-9102" data-mp="3p9uanqqigqna">{STATUS_LABEL[status]}</Badge>
                  </li>)}
              </ul>
              <p className="mt-3 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-9103" data-mp="3u51bmqsebh8i">
                Statuses that carry disciplinary meaning are never displayed outside the student and
                your own staff, regardless of the student's privacy settings.
              </p>
            </div>
          </Panel>

          <Panel title="Accepted documents" data-id="element-9104" data-mp="bvr3neleh2qy">
            <ul className="space-y-1.5" data-id="element-9105" data-mp="grg6bqspuzrh">
              {institution.acceptedDocuments.map(entry => <li key={entry} className="text-body text-ink" data-id="element-9106" data-mp="lsvq7fj5r08a">
                  {entry}
                </li>)}
            </ul>
            <p className="mt-3 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-9107" data-mp="qob0xylvcg0h">
              Campuzon reviews these documents. A document review is labelled as exactly that on the
              student's profile — it never claims your institution confirmed anything.
            </p>
          </Panel>
        </div>}

      {tab === 'Lifecycle' && <div className="space-y-4" data-id="element-9108" data-mp="14v4hv335h6uq">
          <Panel title="Reporting a change" data-id="element-9109" data-mp="14u2xg808v80c">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-9110" data-mp="10tst0zs3opnl">
              When you report a status change, Campuzon moves the academic identity and keeps the
              history. Graduation does not delete the verified relationship — it becomes an alumni
              record, and the profile stops saying “current student” without losing what was true.
            </p>
          </Panel>
          <Panel bodyClassName="p-0" data-id="element-9111" data-mp="1jkk72en1948b">
            <ul className="divide-y divide-line" data-id="element-9112" data-mp="1jjilvhcaz1cv">
              {ourRecords.map(record => {
            const next = nextLifecycle(record.status);
            return <li key={record.id} className="px-5 py-4 sm:px-6" data-id="element-9113" data-mp="86ycgdyemafe">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5" data-id="element-9114" data-mp="36aknq2cppk4">
                      <div className="min-w-0" data-id="element-9115" data-mp="4nhcezn48lwr">
                        <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-9116" data-mp="4osf0w4trnma">
                          <GraduationCapIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-9117" data-mp="9kznowolvd7x" />
                          {record.programme.value}
                        </p>
                        <p className="text-body-sm text-ink-muted" data-id="element-9118" data-mp="kuecgzuq4r4">
                          {record.level?.value ?? '—'} · {record.academicSession}
                        </p>
                        <p className="mt-1 text-body-sm text-ink" data-id="element-9119" data-mp="3munke4zc2ljb">
                          Current status:{' '}
                          <span className="font-medium" data-id="element-9120" data-mp="393ijz0o55p7x">{STATUS_LABEL[record.status]}</span>
                          <span className="text-ink-muted" data-id="element-9121" data-mp="3d4lwpeaw754x">
                            {' '}
                            · last updated {formatDate(record.updatedAt)}
                          </span>
                        </p>
                      </div>
                      {next && <Button variant="secondary" size="sm" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-9123" data-mp="2fjsr6qw1v90y" />} onClick={() => reportStatus(record.id, next, OFFICER)} data-id="element-9122" data-mp="2kidrsrf7rupx">
                          Report as {STATUS_LABEL[next].toLowerCase()}
                        </Button>}
                    </div>
                  </li>;
          })}
            </ul>
          </Panel>
          <Panel title="Revocation" data-id="element-9124" data-mp="2ygty4h95zj7j">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-9125" data-mp="3d5qfj4x428kt">
              You can withdraw a confirmation you previously gave. Doing so removes the badge and is
              recorded with your reason — it never deletes the historical record that the confirmation
              once existed, because that history is what makes the system auditable.
            </p>
          </Panel>
        </div>}
    </div>;
}